import {
  INCOMPLETE_PHQ_Y_VALUE,
} from './constants';
import {
  QuestionAndAnswer,
  LineData,
  DataPoint,
  AssessmentResultAnswer,
  SubAnswer,
  PHQData,
} from '../types';

import {
  Question,
  QuestionType,
  Answer,
  QuestionCondition,
  AssessmentResult,
  AssessmentResultItem,
} from '../types/DataTypes';

import { AssessmentFormState } from '../types/AssessmentTypes';

export const containsAnswer = (answerList: Answer[], possibleAnswer: Answer): boolean => (
  answerList.findIndex((item) => item?.uid === possibleAnswer?.uid) > -1
);

/** Prepare and return the assessment answers to be reported to the backend. */
export const getAssessmentAnswers = (state: AssessmentFormState): string[] => {
  const stateValues = Object.values(state);
  return stateValues.map((stateValue) => {
    const selectedAnswer = stateValue.answer;
    if (selectedAnswer === null || !selectedAnswer?.answers?.length) {
      return '';
    }

    switch (selectedAnswer.type) {
    case 'MULTI_SELECT':
      return JSON.stringify(selectedAnswer.answers.map((answer) => answer.uid));
    case 'MULTIPLE_CHOICE':
      return selectedAnswer.answers[0].uid;
    case 'FREE_FORM_TEXT':
      return selectedAnswer.answers[0].text;
    case 'DATE_FIELD':
      return selectedAnswer.answers[0].text;
    default:
      throw new Error(`Cannot get assessment answers. Got selected answer with unexpected type: ${selectedAnswer}`);
    }
  });
};

/** Return the default assessment result answer based on question type. */
export const defaultAnswerForQuestionType = (questionType: QuestionType): AssessmentResultAnswer => (
  { type: questionType, answers: [] }
);

const isValidCondition = (condition: QuestionCondition): boolean => !!condition
  && !!condition?.dependsOnAnswer
  && !!condition?.dependsOnQuestion;

const conditionIsMetWithoutNegation = (condition: QuestionCondition, state: AssessmentFormState): boolean => {
  if (!isValidCondition(condition)) {
    return false;
  }
  const { questionType: type, ordinal } = condition.dependsOnQuestion;

  const questionAnswer = state[ordinal].answer;
  if (!questionAnswer) {
    return false;
  }
  const subanswers = questionAnswer.answers;
  if (type === 'MULTI_SELECT') {
    return subanswers.map((selected): string => (selected ? selected.uid : '')).includes(condition.dependsOnAnswer.uid);
  }
  if (!subanswers?.length) {
    return false;
  }

  return subanswers[0].uid === condition.dependsOnAnswer.uid || subanswers[0].text === condition.dependsOnAnswer.text;
};

const conditionIsMet = (condition: QuestionCondition, state: AssessmentFormState): boolean => {
  if (!isValidCondition(condition)) {
    return false;
  }

  const isMetWithoutNegation = conditionIsMetWithoutNegation(condition, state);
  return condition.isNegated ? !isMetWithoutNegation : isMetWithoutNegation;
};

export const shouldShowQuestion = (
  question: Question, state: AssessmentFormState,
): boolean => question.conditions?.every(
  (condition) => conditionIsMet(condition, state),
);

export const getAssessmentResultItemSubAnswers = (
  assessmentResultItem: AssessmentResultItem,
): SubAnswer[] => {
  const {
    question,
    textValue,
    dateValue,
    selectedAnswers,
  } = assessmentResultItem;
  const { questionType } = question;

  if (questionType === 'FREE_FORM_TEXT') {
    return [{ uid: textValue.uid, text: textValue.text }];
  } else if (questionType === 'DATE_FIELD') {
    return [{ uid: null, text: new Date(dateValue).toISOString() }];
  } else {
    return selectedAnswers.map((answer) => ({
      uid: answer.uid,
      text: answer.text,
    }));
  }
};

export const assessmentResultItemToAssessmentResultAnswer = (
  assessmentResultItem: AssessmentResultItem,
): AssessmentResultAnswer => ({
  type: assessmentResultItem.question.questionType,
  answers: getAssessmentResultItemSubAnswers(assessmentResultItem),
});

// Creates a map of question UID -> AssessmentResultAnswer
const getAssessmentResultAnswerMap = (
  assessmentResultItems: AssessmentResultItem[],
): Record<string, AssessmentResultAnswer> => {
  const answerMap: Record<string, AssessmentResultAnswer> = {};

  assessmentResultItems.forEach((assessmentResultItem) => {
    const questionUid = assessmentResultItem.question.uid;
    const assessmentResultAnswer = assessmentResultItemToAssessmentResultAnswer(assessmentResultItem);
    answerMap[questionUid] = assessmentResultAnswer;
  });

  return answerMap;
};

export const getInitialAssessmentFormState = (
  questions: Question[],
  assessmentResult?: AssessmentResult,
): AssessmentFormState => {
  const questionToAnswerMap = getAssessmentResultAnswerMap(
    assessmentResult?.assessmentResultItems ?? [],
  )
  // Initialize the state assuming no questions are hidden
  const initialState = questions?.reduce((accum, question) => {
    const defaultAnswers = defaultAnswerForQuestionType(question.questionType);
    const answer = questionToAnswerMap[question.uid] ?? defaultAnswers;
    return Object.assign(accum, {
      [question.ordinal]: {
        inErrorState: false,
        hidden: false,
        question,
        answer,
      },
    });
  }, {} as AssessmentFormState);

  // Hide questions whose conditions are not met.
  Object.keys(initialState).forEach((questionOrdinal) => {
    const ordinal = parseInt(questionOrdinal, 10);
    initialState[ordinal].hidden = !shouldShowQuestion(initialState[ordinal].question, initialState);
  });

  return initialState;
};

export const getAnswerUidToAnswerMap = (answers: Answer[]): { [key: string]: SubAnswer} => (
  answers.reduce((accumulated, possibleAnswer) => (
    Object.assign(accumulated, {
      [possibleAnswer.uid]: {
        text: possibleAnswer.text,
        uid: possibleAnswer.uid,
      },
    })
  ), {} as Record<string, SubAnswer>)
);

export const matchAnswersToQuestions = (questions: Question[], answerUids: string[]): QuestionAndAnswer[] => {
  if (questions.length !== answerUids.length) {
    throw new Error('Lengths do not match');
  }
  return questions.map((question, index): QuestionAndAnswer => {
    const selectedAnswer = answerUids[index];
    const questionText = question.text;
    const matchingAnswer = question.answers.find((answer) => answer.uid === selectedAnswer);
    const selectedAnswerValue = matchingAnswer ? `${matchingAnswer.assessmentValue}` : '-';
    const selectedAnswerText = matchingAnswer ? `${matchingAnswer.text}` : '';
    return {
      index,
      questionText,
      selectedAnswerValue,
      selectedAnswerText,
      expanded: false,
    };
  });
};

export const resultHasSkippedQuestions = (assessmentResult: AssessmentResult): boolean => {
  const answers = assessmentResult?.assessmentResultItems;
  const questions = assessmentResult?.assessment?.questions;
  return !answers || !questions || answers.length < questions.length;
};

export const mapDateToScore = (phqs: PHQData[]): Map<number, string | number> => (
  phqs.reduce((accumulator, phq) => {
    const displayScore = !phq.hasSkippedQuestions;
    accumulator.set(
      (new Date(phq.completeTime)).getTime(),
      displayScore ? phq.score : '',
    );
    return accumulator;
  }, new Map<number, string | number>())
);

type FormatChartDataType = {
  phqData: LineData;
  dateScoreMap: Map<number, string | number>;
}
export const formatChartData = (
  userUid: string,
  phqs: PHQData[],
): FormatChartDataType => {
  const dateScoreMap = mapDateToScore(phqs);

  if (!phqs?.length) {
    return {
      dateScoreMap,
      phqData: {
        data: [],
        id: userUid,
      },
    };
  }

  const dataPoints = phqs.map((phq): DataPoint => {
    return (
      {
        x: new Date(phq.completeTime),
        y: !phq.hasSkippedQuestions ? phq.score : INCOMPLETE_PHQ_Y_VALUE,
      }
    );
  });

  dataPoints.sort((a, b) => ((a.x > b.x) ? 1 : -1));

  return {
    dateScoreMap,
    phqData: {
      data: dataPoints,
      id: userUid,
    },
  };
};
